import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  FileText, 
  Video, 
  Music, 
  Image, 
  Package, 
  File,
  ExternalLink,
  Clock,
  User,
  Calendar,
  Download,
  RefreshCw,
  Loader2
} from "lucide-react";
import { DigitalContentPurchase } from "@/types/digitalContent";
import { getUserPurchasedContents } from "@/services/digitalContent/purchaseService";
import { CONTENT_TYPE_LABELS } from "@/types/digitalContent";

const getContentTypeIcon = (contentType: string) => {
  const icons = {
    pdf: <FileText className="h-4 w-4" />,
    video: <Video className="h-4 w-4" />,
    audio: <Music className="h-4 w-4" />,
    image: <Image className="h-4 w-4" />,
    zip: <Package className="h-4 w-4" />,
    document: <File className="h-4 w-4" />
  };
  return icons[contentType as keyof typeof icons] || <File className="h-4 w-4" />;
};

const getContentTypeColor = (contentType: string): string => {
  const colors = {
    pdf: 'bg-red-500/20 text-red-400 border-red-500/30',
    video: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    audio: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    image: 'bg-green-500/20 text-green-400 border-green-500/30',
    zip: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    document: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };
  return colors[contentType as keyof typeof colors] || colors.document;
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatPrice = (priceCents: number): string => {
  if (priceCents === 0) {
    return 'Gratuito';
  }
  return (priceCents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const MeusConteudosComprados = () => {
  const [purchases, setPurchases] = useState<DigitalContentPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log("📚 [MeusConteudosComprados - Mentor] Carregando conteúdos comprados...");
      
      // Reutilizar o serviço que busca por buyer_id (funciona independente do role)
      const data = await getUserPurchasedContents();
      
      console.log("📚 [MeusConteudosComprados - Mentor] Conteúdos carregados:", {
        total: data.length,
        conteudos: data.map(p => ({
          id: p.id,
          title: p.digital_content?.title,
          paymentStatus: p.payment_status,
          createdAt: p.created_at,
          accessGrantedAt: p.access_granted_at
        }))
      });
      
      setPurchases(data);
    } catch (err: any) {
      console.error("❌ [MeusConteudosComprados - Mentor] Erro ao carregar conteúdos:", err);
      setError(err.message || "Erro ao carregar seus conteúdos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  const handleAccessContent = (fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 max-w-6xl mx-auto">
        <div className="text-center py-12">
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={fetchPurchases}>
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Conteúdos Comprados</h1>
          <p className="text-gray-400 mt-1">
            Histórico de conteúdos digitais que você adquiriu como jogador
          </p>
        </div>
        <Button 
          variant="outline" 
          size="icon"
          onClick={fetchPurchases}
          disabled={loading}
          className="h-9 w-9 self-start sm:self-auto"
          title="Atualizar conteúdos"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#00B6F0]/20 rounded-lg">
                <Package className="h-5 w-5 text-[#00B6F0]" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Total de Conteúdos</p>
                <p className="text-xl font-bold text-white">{purchases.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Download className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Downloads Realizados</p>
                <p className="text-xl font-bold text-white">
                  {purchases.reduce((total, purchase) => total + purchase.download_count, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Último Acesso</p>
                <p className="text-xl font-bold text-white">
                  {purchases.length > 0 ? formatDate(purchases[0].created_at) : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      {purchases.length === 0 ? (
        <Card className="bg-[#1E293B] border-[#2A3749]">
          <CardContent className="text-center py-12">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Nenhum conteúdo encontrado
            </h3>
            <p className="text-gray-400 mb-6">
              Você ainda não adquiriu nenhum conteúdo digital. Explore os perfis dos mentores para encontrar materiais interessantes!
            </p>
            <Button 
              onClick={() => window.location.href = '/mentores'}
              className="bg-[#00B6F0] hover:bg-[#0099CC] text-black"
            >
              Explorar Mentores
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {purchases.map((purchase) => {
            const content = purchase.digital_content;
            if (!content) return null;

            return (
              <Card
                key={purchase.id}
                className="bg-[#1E293B] border-[#2A3749] overflow-hidden hover:border-[#00B6F0]/50 transition-colors"
              >
                {/* Imagem de Capa */}
                {content.cover_image_url && (
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={content.cover_image_url}
                      alt={content.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1E293B]/80 to-transparent" />
                  </div>
                )}

                <CardContent className="p-4">
                  {/* Tipo e Status */}
                  <div className="flex items-center justify-between mb-3">
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${getContentTypeColor(content.content_type)}`}
                    >
                      {getContentTypeIcon(content.content_type)}
                      <span className="ml-1">{CONTENT_TYPE_LABELS[content.content_type]}</span>
                    </Badge>
                    
                    <Badge variant="outline" className="text-xs text-green-400 border-green-500/30">
                      Adquirido
                    </Badge>
                  </div>

                  {/* Título */}
                  <h3 className="font-semibold text-white mb-2 line-clamp-2 leading-tight">
                    {content.title}
                  </h3>

                  {/* Descrição */}
                  <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                    {content.description}
                  </p>

                  {/* Informações do Mentor */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <User className="h-3 w-3" />
                    <span>Por {content.mentor?.profiles?.nickname || 'Mentor'}</span>
                  </div>

                  {/* Informações adicionais */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    <div className="flex items-center gap-3">
                      {content.estimated_duration_minutes && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>{content.estimated_duration_minutes}min</span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-1">
                        <Download className="h-3 w-3" />
                        <span>{purchase.download_count} acessos</span>
                      </div>
                    </div>
                    
                    <div className="text-[#00B6F0] font-medium">
                      {formatPrice(purchase.purchase_price_cents)}
                    </div>
                  </div>

                  {/* Tags */}
                  {content.tags && content.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {content.tags.slice(0, 2).map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs text-gray-400 border-gray-600">
                          {tag}
                        </Badge>
                      ))}
                      {content.tags.length > 2 && (
                        <Badge variant="outline" className="text-xs text-gray-400 border-gray-600">
                          +{content.tags.length - 2}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Data de Compra */}
                  <div className="text-xs text-gray-500 mb-4">
                    Adquirido em {formatDate(purchase.created_at)}
                  </div>

                  {/* Botão de Acesso */}
                  <Button
                    onClick={() => handleAccessContent(content.file_url)}
                    className="w-full bg-[#00B6F0] hover:bg-[#0099CC] text-black font-medium"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Acessar Conteúdo
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MeusConteudosComprados;

